import * as XLSX from "xlsx";

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_DATA_ROWS = 2000;

export type BankStatementDateFormat = "dmy" | "mdy";
export type BankStatementAmountMode = "single" | "debit-credit";

export type BankStatementColumnMapping = {
  date: number;
  description: number;
  reference: number;
  amountMode: BankStatementAmountMode;
  amount: number;
  debit: number;
  credit: number;
};

export type BankStatementCsvTable = {
  headers: string[];
  rows: unknown[][];
};

export type ParsedBankStatementTransaction = {
  rowNumber: number;
  transactionDate: string;
  description: string;
  reference?: string;
  amountKobo: string;
};

export type BankStatementParseResult = {
  transactions: ParsedBankStatementTransaction[];
  errors: Array<{ rowNumber: number; message: string }>;
  skippedRows: number;
};

function normalizedHeader(value: unknown): string {
  return String(value ?? "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
}

function findColumn(headers: string[], aliases: string[]): number {
  return headers.findIndex((header) => aliases.includes(header));
}

export function suggestBankStatementMapping(headers: string[]): BankStatementColumnMapping {
  const normalized = headers.map(normalizedHeader);
  const debit = findColumn(normalized, ["debit", "debitamount", "dr", "withdrawal", "withdrawals"]);
  const credit = findColumn(normalized, ["credit", "creditamount", "cr", "deposit", "deposits"]);
  const amount = findColumn(normalized, ["amount", "transactionamount", "amountngn", "value"]);

  return {
    date: findColumn(normalized, [
      "date", "transactiondate", "valuedate", "postdate", "transactiondatetime",
    ]),
    description: findColumn(normalized, [
      "description", "narration", "details", "remarks", "transactiondetails", "transactionnarration",
    ]),
    reference: findColumn(normalized, [
      "reference", "ref", "transactionreference", "transactionid", "sessionid",
    ]),
    amountMode: debit >= 0 && credit >= 0 ? "debit-credit" : "single",
    amount,
    debit,
    credit,
  };
}

export async function readBankStatementCsv(file: File): Promise<BankStatementCsvTable> {
  if (!file.name.toLowerCase().endsWith(".csv")) {
    throw new Error("Choose a CSV file exported from your bank.");
  }
  if (file.size > MAX_FILE_BYTES) {
    throw new Error("This CSV is larger than 5 MB. Split it into smaller statements.");
  }

  const workbook = XLSX.read(await file.arrayBuffer(), { type: "array", raw: true, cellDates: true });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) throw new Error("The CSV file is empty.");

  const rows = XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[sheetName], {
    header: 1,
    defval: "",
    raw: true,
    blankrows: true,
  });
  if (rows.length < 2) throw new Error("The CSV needs a header row and at least one transaction row.");
  if (rows.length - 1 > MAX_DATA_ROWS) {
    throw new Error(`This CSV contains more than ${MAX_DATA_ROWS.toLocaleString()} rows. Split it into smaller statements.`);
  }

  return {
    headers: rows[0].map((value) => String(value ?? "").trim()),
    rows: rows.slice(1),
  };
}

function formatDateParts(year: number, month: number, day: number): string | null {
  if (year < 1900 || year > 2200 || month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return `${year.toString().padStart(4, "0")}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
}

function parseDate(value: unknown, dateFormat: BankStatementDateFormat): string | null {
  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return formatDateParts(value.getUTCFullYear(), value.getUTCMonth() + 1, value.getUTCDate());
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    const parts = XLSX.SSF.parse_date_code(value);
    return parts ? formatDateParts(parts.y, parts.m, parts.d) : null;
  }

  const text = String(value ?? "").trim();
  if (!text) return null;

  const iso = text.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[ T].*)?$/);
  if (iso) return formatDateParts(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  const numeric = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})(?:\s.*)?$/);
  if (numeric) {
    const first = Number(numeric[1]);
    const second = Number(numeric[2]);
    let year = Number(numeric[3]);
    if (year < 100) year += year < 50 ? 2000 : 1900;
    return dateFormat === "dmy"
      ? formatDateParts(year, second, first)
      : formatDateParts(year, first, second);
  }

  const parsed = new Date(text);
  if (Number.isFinite(parsed.getTime()) && /[a-z]/i.test(text)) {
    return formatDateParts(parsed.getUTCFullYear(), parsed.getUTCMonth() + 1, parsed.getUTCDate());
  }
  return null;
}

function parseMoneyToKobo(value: unknown): bigint | null {
  if (value === null || value === undefined || String(value).trim() === "") return null;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    const scaled = value * 100;
    const rounded = Math.round(scaled);
    if (Math.abs(scaled - rounded) > 0.000001) return null;
    return BigInt(rounded);
  }

  let text = String(value).trim().replace(/\u00a0/g, " ");
  const parenthesized = text.startsWith("(") && text.endsWith(")");
  if (parenthesized) text = text.slice(1, -1);
  text = text.replace(/^(?:NGN|N)\s*/i, "").replace(/₦/g, "").replace(/[,\s]/g, "");
  const match = text.match(/^([+-]?)(\d+)(?:\.(\d{1,2}))?$/);
  if (!match) return null;
  const sign = parenthesized || match[1] === "-" ? -1n : 1n;
  const major = BigInt(match[2]);
  const minor = BigInt((match[3] ?? "").padEnd(2, "0"));
  return sign * (major * 100n + minor);
}

function cell(row: unknown[], column: number): unknown {
  return column < 0 ? "" : row[column] ?? "";
}

export function parseBankStatementRows(
  headers: string[],
  rows: unknown[][],
  mapping: BankStatementColumnMapping,
  dateFormat: BankStatementDateFormat,
): BankStatementParseResult {
  const errors: BankStatementParseResult["errors"] = [];
  const transactions: BankStatementParseResult["transactions"] = [];
  let skippedRows = 0;

  if (mapping.date < 0 || mapping.description < 0) {
    return {
      transactions,
      errors: [{ rowNumber: 1, message: "Select both the date and description columns." }],
      skippedRows,
    };
  }
  if (mapping.date >= headers.length || mapping.description >= headers.length ||
      (mapping.reference >= headers.length) ||
      (mapping.amountMode === "single" && mapping.amount >= headers.length) ||
      (mapping.amountMode === "debit-credit" &&
        (mapping.debit >= headers.length || mapping.credit >= headers.length))) {
    return {
      transactions,
      errors: [{ rowNumber: 1, message: "One or more selected columns are no longer available." }],
      skippedRows,
    };
  }
  if (mapping.amountMode === "single" && mapping.amount < 0) {
    return {
      transactions,
      errors: [{ rowNumber: 1, message: "Select the signed amount column." }],
      skippedRows,
    };
  }
  if (mapping.amountMode === "debit-credit" && (mapping.debit < 0 || mapping.credit < 0)) {
    return {
      transactions,
      errors: [{ rowNumber: 1, message: "Select both the debit and credit columns." }],
      skippedRows,
    };
  }

  rows.forEach((row, index) => {
    const rowNumber = index + 2;
    if (!row.some((value) => String(value ?? "").trim() !== "")) {
      skippedRows++;
      return;
    }

    const dateValue = cell(row, mapping.date);
    const descriptionValue = String(cell(row, mapping.description) ?? "").trim();
    const amountValue = mapping.amountMode === "single"
      ? parseMoneyToKobo(cell(row, mapping.amount))
      : null;
    const singleAmountRaw = mapping.amountMode === "single"
      ? String(cell(row, mapping.amount) ?? "").trim()
      : "";
    const debitRaw = mapping.amountMode === "debit-credit"
      ? String(cell(row, mapping.debit) ?? "").trim()
      : "";
    const creditRaw = mapping.amountMode === "debit-credit"
      ? String(cell(row, mapping.credit) ?? "").trim()
      : "";
    const debitValue = mapping.amountMode === "debit-credit"
      ? parseMoneyToKobo(cell(row, mapping.debit))
      : null;
    const creditValue = mapping.amountMode === "debit-credit"
      ? parseMoneyToKobo(cell(row, mapping.credit))
      : null;
    const rawAmountCell = mapping.amountMode === "single"
      ? String(cell(row, mapping.amount) ?? "").trim()
      : `${String(cell(row, mapping.debit) ?? "").trim()}${String(cell(row, mapping.credit) ?? "").trim()}`;

    if (!String(dateValue ?? "").trim() && !rawAmountCell) {
      skippedRows++;
      return;
    }

    const transactionDate = parseDate(dateValue, dateFormat);
    if (!transactionDate) {
      errors.push({ rowNumber, message: "Date is missing or not recognized." });
      return;
    }
    if (!descriptionValue) {
      errors.push({ rowNumber, message: "Description is required." });
      return;
    }
    if (descriptionValue.length > 500) {
      errors.push({ rowNumber, message: "Description exceeds 500 characters." });
      return;
    }

    let signedAmount: bigint | null;
    if (mapping.amountMode === "single") {
      if (singleAmountRaw && amountValue === null) {
        errors.push({ rowNumber, message: "Amount is invalid or has more than two decimal places." });
        return;
      }
      signedAmount = amountValue;
    } else {
      if ((debitRaw && debitValue === null) || (creditRaw && creditValue === null)) {
        errors.push({ rowNumber, message: "Debit or credit value is invalid." });
        return;
      }
      if (debitValue !== null && debitValue !== 0n && creditValue !== null && creditValue !== 0n) {
        errors.push({ rowNumber, message: "Both debit and credit have values; check the column mapping." });
        return;
      }
      if (debitValue !== null && debitValue !== 0n) {
        signedAmount = debitValue < 0n ? debitValue : -debitValue;
      } else if (creditValue !== null && creditValue !== 0n) {
        signedAmount = creditValue < 0n ? -creditValue : creditValue;
      } else {
        signedAmount = null;
      }
    }

    if (signedAmount === null) {
      errors.push({ rowNumber, message: "Amount is missing or has more than two decimal places." });
      return;
    }
    if (signedAmount === 0n) {
      errors.push({ rowNumber, message: "Zero-value lines cannot be reconciled." });
      return;
    }
    if (signedAmount.toString().replace("-", "").length > 18) {
      errors.push({ rowNumber, message: "Amount is outside the supported range." });
      return;
    }

    const referenceValue = mapping.reference >= 0
      ? String(cell(row, mapping.reference) ?? "").trim()
      : "";
    if (referenceValue.length > 180) {
      errors.push({ rowNumber, message: "Reference exceeds 180 characters." });
      return;
    }

    transactions.push({
      rowNumber,
      transactionDate,
      description: descriptionValue,
      ...(referenceValue ? { reference: referenceValue } : {}),
      amountKobo: signedAmount.toString(),
    });
  });

  if (transactions.length === 0 && errors.length === 0) {
    errors.push({ rowNumber: 1, message: "No transaction rows were found." });
  }

  return { transactions, errors, skippedRows };
}

export function columnLabel(headers: string[], index: number): string {
  return index >= 0 ? headers[index] ?? `Column ${index + 1}` : "Not selected";
}
